import { FailureFilters } from '@/types/filters';
import { FailureRecord } from '@/types/failure';
import { TurbineForForecast } from '@/types/forecast';
import { PlannedFailuresByPeriod } from '@/types/workerTypes';
import { createPeriodRangeLabel } from '@/utils/forecastUtils';

export const validateSubset = (
  currentFilter: string[] | undefined,
  sourceFilter: string[] | undefined,
  filterName: string
): string | null => {
  if (!currentFilter || currentFilter.length === 0) return null;
  if (!sourceFilter || sourceFilter.length === 0) return null;

  const invalidOptions = currentFilter.filter(option => !sourceFilter.includes(option));
  if (invalidOptions.length > 0) {
    return `${filterName} contains options not in source analysis: ${invalidOptions.join(', ')}`;
  }
  return null;
};

export const createTurbineMap = (failures: FailureRecord[]): Map<string, TurbineForForecast> => {
  const turbineMap = new Map<string, TurbineForForecast>();
  const now = new Date();

  failures.forEach((failure: FailureRecord) => {
    // Calculate current operational time for forecasting
    let currentOperationTimeYears = failure.operation_time_years;

    // For planned failures with future restoration dates, recalculate to current time
    if (failure.outcome_name === 'Planned') {
      const restorationDate = failure.restoration_date ? new Date(failure.restoration_date) : null;

      if (restorationDate && restorationDate > now) {
        // Treat as censored (still operating) - calculate time to now, not to future planned date
        const installDate = failure.install_date ? new Date(failure.install_date) :
          failure.commissioning_date ? new Date(failure.commissioning_date) : now;

        const daysSinceInstall = Math.floor((now.getTime() - installDate.getTime()) / (1000 * 60 * 60 * 24));
        const adjustedYears = Math.max(0, daysSinceInstall) / 365.25;

        currentOperationTimeYears = adjustedYears;
      }
    }

    // Filter out negative or zero operational times (data quality issues)
    if (currentOperationTimeYears <= 0) {
      return;
    }

    const turbineKey = `${failure.site_name}-${failure.turbine_name}`;
    if (!turbineMap.has(turbineKey)) {
      turbineMap.set(turbineKey, {
        turbineId: failure.turbine_name || 'unknown',
        siteName: failure.site_name || 'unknown',
        installationDate: failure.commissioning_date || null,
        turbineModel: failure.turbine_model || null,
        failures: [],
        currentAge: currentOperationTimeYears
      });
    }
    turbineMap.get(turbineKey)!.failures.push(failure);
  });

  return turbineMap;
};

export const applyTurbineFilters = (
  turbines: TurbineForForecast[],
  filters: FailureFilters,
  _excludeComponents = true
): TurbineForForecast[] => {
  return turbines.filter(turbine => {
    if (!turbine.installationDate) return false;

    let matches = true;

    // Site filter - empty array means "all sites"
    if (filters.sites && filters.sites.length > 0) {
      matches = matches && filters.sites.includes(turbine.siteName);
    }

    // Skip component filtering if excludeComponents is true
    // Component filters only affect which failures are analyzed, not which turbines are counted

    // Turbine model filter - empty array means "all models"
    if (filters.turbineModels && filters.turbineModels.length > 0) {
      matches = matches && turbine.turbineModel && filters.turbineModels.includes(turbine.turbineModel);
    }

    return matches;
  });
};

export const aggregatePlannedFailures = (
  failures: FailureRecord[],
  filters: FailureFilters,
  horizonYears: number,
  granularityMonths: number,
  startDate?: string
): PlannedFailuresByPeriod => {
  const forecastStart = startDate ? new Date(startDate) : new Date();
  const today = new Date();

  const effectivePlannedStart = forecastStart < today ? today : forecastStart;

  const endDate = new Date(forecastStart);
  endDate.setFullYear(endDate.getFullYear() + horizonYears);

  const plannedFailures = failures.filter(failure => {
    if (failure.outcome_name !== 'Planned' || filters.excludedCaseIds.includes(String(failure.case_id))) {
      return false;
    }

    const failureDate = new Date(failure.failure_date);
    if (failureDate < effectivePlannedStart || failureDate > endDate) {
      return false;
    }

    if (filters.sites && filters.sites.length > 0) {
      if (!filters.sites.includes(failure.site_name)) {
        return false;
      }
    }

    if (filters.componentsMajor && filters.componentsMajor.length > 0) {
      if (!filters.componentsMajor.includes(failure.component_major)) {
        return false;
      }
    }

    if (filters.turbineModels && filters.turbineModels.length > 0) {
      if (!filters.turbineModels.includes(failure.turbine_model)) {
        return false;
      }
    }

    if (filters.failureModes && filters.failureModes.length > 0) {
      if (!filters.failureModes.includes(failure.failure_mode)) {
        return false;
      }
    }

    return true;
  });

  const grouped: PlannedFailuresByPeriod = {};

  plannedFailures.forEach(failure => {
    const failureDate = new Date(failure.failure_date);

    const monthsFromStart = (
      (failureDate.getFullYear() - forecastStart.getFullYear()) * 12 +
      (failureDate.getMonth() - forecastStart.getMonth())
    );

    const aggregatedPeriodIndex = Math.floor(monthsFromStart / granularityMonths) * granularityMonths;
    const timePeriod = createPeriodRangeLabel(forecastStart, granularityMonths, aggregatedPeriodIndex);

    if (!grouped[timePeriod]) {
      grouped[timePeriod] = {
        count: 0,
        details: []
      };
    }

    grouped[timePeriod].count++;
    grouped[timePeriod].details?.push({
      turbineId: failure.turbine_name || 'unknown',
      turbineName: failure.turbine_name,
      siteName: failure.site_name,
      plannedDate: failure.failure_date,
      component: failure.component_major || 'unknown',
      description: failure.failure_mode
    });
  });

  return grouped;
};